import useTranslator from "@pma/hooks/use-translator"
import { useEffect, useState } from "react"
import { Controller, useFormContext as useRHFContext } from "react-hook-form"

interface Resource {
  id: string
  value: string
}

interface Props {
  name: string
  options: Resource[]
  value: string | number
  onChange?: (value: string | number) => void
  freesolo?: boolean
  useForm?: boolean
  placeholder?: string
  required?: boolean
  showAllOnSelect?: boolean
}

export function AutoCompleteInput({
  name,
  options,
  value,
  onChange,
  freesolo = false,
  useForm = true,
  placeholder,
  required,
  showAllOnSelect = false,
}: Props) {
  const { translate } = useTranslator()

  const context = useForm ? useRHFContext() : null
  const control = context?.control
  const [inputValue, setInputValue] = useState("")
  const [showOptions, setShowOptions] = useState(false)
  const [isSelected, setIsSelected] = useState(false)

  // Sync initial value (id or text) to input text
  useEffect(() => {
    const match = options?.find((opt) => opt.id === value)
    if (match) {
      setInputValue(match.value)
      setIsSelected(true)
    } else if (typeof value === "string") {
      setInputValue(value)
    }
  }, [value, options])

  const filteredOptions = options?.filter((opt) =>
    opt.value.toLowerCase().includes(inputValue.toLowerCase()),
  )

  const handleInputChange = (inputVal: string, rhfOnChange?: (val: any) => void) => {
    setInputValue(inputVal)
    setShowOptions(true)
    setIsSelected(false)

    if (freesolo) {
      rhfOnChange?.(inputVal)
      onChange?.(inputVal)
    } else {
      if (inputVal === "") {
        rhfOnChange?.("")
        onChange?.("")
      }
    }
    // if freesolo === false: only update input, don't change value
  }

  const handleOptionSelect = (selected: Resource, rhfOnChange?: (val: any) => void) => {
    rhfOnChange?.(selected.id)
    onChange?.(selected.id)
    setInputValue(selected.value)
    setShowOptions(false)
    setIsSelected(true)
  }
  const computedPlaceholder = required
    ? `${translate(placeholder ?? "Select or type a resource")} *`
    : translate(placeholder ?? "Select or type a resource")

  const renderInput = (field?: any, fieldState?: any) => (
    <div className='relative'>
      <input
        type='text'
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value, field?.onChange)}
        onFocus={() => setShowOptions(true)}
        onBlur={() => setTimeout(() => setShowOptions(false), 100)}
        className={`w-full rounded-md border px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 ${
          fieldState?.error
            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
            : 'border-gray-300 focus:ring-blue-500'
        }`}
        placeholder={computedPlaceholder}
      />

      {/* Clear Button */}
      {inputValue && (
        <button
          type='button'
          className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500'
          onClick={() => {
            setInputValue("")
            setShowOptions(false)
            setIsSelected(false)
            field?.onChange?.("")
            onChange?.("")
          }}
        >
          ×
        </button>
      )}

      {showOptions && (showAllOnSelect && isSelected ? options : filteredOptions)?.length > 0 && (
        <ul className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg'>
          {(showAllOnSelect && isSelected ? options : filteredOptions).map((opt) => (
            <li
              key={opt.id}
              onMouseDown={() => handleOptionSelect(opt, field?.onChange)}
              className={`cursor-pointer px-3 py-2 ${opt.id === value ? 'bg-blue-100 font-medium text-blue-700' : 'hover:bg-blue-100'}`}
            >
              {opt.value}
            </li>
          ))}
        </ul>
      )}

      {fieldState?.error && <p className='mt-1 text-sm text-red-500'>{fieldState.error.message}</p>}
    </div>
  )

  if (control) {
    return (
      <Controller
        control={control}
        name={name}
        defaultValue={value}
        render={({ field, fieldState }) => renderInput(field, fieldState)}
      />
    )
  }

  return renderInput()
}
